import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';
import type { FactureStatut, ModePaiementFacture, PieceJointe, TypeDocFacture } from '@/services/achats';

export interface LigneFactureDTO {
  id: string;
  designation: string;
  unite: string | null;
  quantite: number;
  pu_ht: number;
  taux_tva: number;
  remise_pct: number;
  montant_ht: number;
}
export interface LigneFactureInputT {
  designation: string;
  unite?: string | null;
  quantite: number;
  pu_ht: number;
  taux_tva: number;
  remise_pct?: number;
}

/* Onglet « Production » des Factures : factures clients (TVA collectée), encaissements
   (G50 à l'encaissement). Miroir de l'onglet Achat. */

export type StatutEncaissement = 'NON_ENCAISSEE' | 'PARTIELLE' | 'SOLDEE';
export const STATUT_ENCAISSEMENT_LABEL: Record<StatutEncaissement, string> = {
  NON_ENCAISSEE: 'Non encaissée',
  PARTIELLE: 'Part. encaissée',
  SOLDEE: 'Soldée',
};

export interface EncaissementDTO {
  id: string;
  facture_id: string;
  date_encaissement: string;
  periode_mois: number;
  periode_annee: number;
  montant_encaisse: number;
  ht_encaisse: number;
  tva_encaisse: number;
  mode_paiement: ModePaiementFacture | null;
  paiement_reference: string | null;
  paiement_banque: string | null;
  paiement_echeance: string | null;
  justificatif_url: string | null;
}

export interface EncaissementInput {
  date_encaissement: string;
  montant_encaisse: number;
  periode_mois?: number | null;
  periode_annee?: number | null;
  mode_paiement?: ModePaiementFacture | null;
  paiement_reference?: string | null;
  paiement_banque?: string | null;
  paiement_echeance?: string | null;
  justificatif_url?: string | null;
}

export type OrigineFacture = 'DEVIS' | 'DIRECTE';

export interface FactureVenteDTO {
  id: string;
  reference: string | null;
  type_doc: TypeDocFacture;
  origine: OrigineFacture;
  comptabilisee: boolean;
  numero_facture: string | null;
  facture_origine_id: string | null;
  client_id: string | null;
  chantier_id: string | null;
  date_facture: string | null;
  date_echeance: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_exonere: boolean;
  tva_montant: number;
  montant_ttc: number;
  retenue_garantie_pct: number;
  mode_paiement: ModePaiementFacture | null;
  paiement_reference: string | null;
  paiement_banque: string | null;
  paiement_date: string | null;
  paiement_echeance: string | null;
  justificatif_url: string | null;
  pieces: PieceJointe[];
  statut: FactureStatut;
  date_validation: string | null;
  date_paiement: string | null;
  notes: string | null;
  lignes: LigneFactureDTO[];
  encaissements: EncaissementDTO[];
  total_encaisse: number;
  reste_a_encaisser: number;
  retenue_garantie_montant: number;
  statut_encaissement: StatutEncaissement;
  // Lien situation → facture (génération assistée) + contrôle d'écart.
  situation_id: string | null;
  avance_a_rembourser: number;
  net_a_encaisser: number;
  ecart_situation: number;
  aligne_situation: boolean;
}

export async function delierSituation(id: string, motif: string): Promise<FactureVenteDTO> {
  const { data } = await api.post<FactureVenteDTO>(`/factures-vente/${id}/delier`, { motif });
  return data;
}

export interface FactureVenteInput {
  type_doc?: TypeDocFacture;
  comptabilisee?: boolean;
  numero_facture?: string | null;
  client_id?: string | null;
  chantier_id?: string | null;
  date_facture?: string | null;
  date_echeance?: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_exonere?: boolean;
  tva_montant?: number | null;
  montant_ttc?: number | null;
  retenue_garantie_pct?: number;
  mode_paiement?: ModePaiementFacture | null;
  paiement_reference?: string | null;
  paiement_banque?: string | null;
  paiement_date?: string | null;
  paiement_echeance?: string | null;
  statut?: FactureStatut | null;
  date_paiement?: string | null;
  justificatif_url?: string | null;
  pieces?: PieceJointe[];
  notes?: string | null;
  nature?: string | null;
  lignes?: LigneFactureInputT[];
}

export interface FactureVenteFiltres {
  mois?: number | null;
  annee?: number | null;
  mois_encaissement?: number | null;
  type_doc?: TypeDocFacture | null;
  comptabilisee?: boolean | null;
  client_id?: string | null;
  statut?: FactureStatut | null;
  chantier_id?: string | null;
  mode_paiement?: ModePaiementFacture | null;
  tva_pct?: number | null;
}

export interface FactureVenteTotaux {
  nombre: number;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  total_encaisse_periode: number;
}

export async function listFacturesVente(filtres?: FactureVenteFiltres): Promise<Page<FactureVenteDTO>> {
  const { data } = await api.get<Page<FactureVenteDTO>>('/factures-vente', { params: filtres ?? {} });
  return data;
}
export async function getFacturesVenteTotaux(filtres?: FactureVenteFiltres): Promise<FactureVenteTotaux> {
  const { data } = await api.get<FactureVenteTotaux>('/factures-vente/totaux', { params: filtres ?? {} });
  return data;
}
export async function createFactureVente(payload: FactureVenteInput): Promise<FactureVenteDTO> {
  const { data } = await api.post<FactureVenteDTO>('/factures-vente', payload);
  return data;
}
export async function updateFactureVente(id: string, payload: Partial<FactureVenteInput>): Promise<FactureVenteDTO> {
  const { data } = await api.put<FactureVenteDTO>(`/factures-vente/${id}`, payload);
  return data;
}
export async function deleteFactureVente(id: string): Promise<void> {
  await api.delete(`/factures-vente/${id}`);
}
export async function validerFactureVente(id: string): Promise<FactureVenteDTO> {
  const { data } = await api.post<FactureVenteDTO>(`/factures-vente/${id}/valider`);
  return data;
}
export async function addEncaissement(factureId: string, payload: EncaissementInput): Promise<FactureVenteDTO> {
  const { data } = await api.post<FactureVenteDTO>(`/factures-vente/${factureId}/encaissements`, payload);
  return data;
}
export async function updateEncaissement(id: string, payload: EncaissementInput): Promise<FactureVenteDTO> {
  const { data } = await api.put<FactureVenteDTO>(`/encaissements/${id}`, payload);
  return data;
}
export async function deleteEncaissement(id: string): Promise<void> {
  await api.delete(`/encaissements/${id}`);
}
/** Crée un avoir (note de crédit) à partir d'une facture : lignes en crédit, lien d'origine. */
export async function creerAvoir(factureId: string, payload?: { date_facture?: string | null; motif?: string | null }): Promise<FactureVenteDTO> {
  const { data } = await api.post<FactureVenteDTO>(`/factures-vente/${factureId}/avoir`, payload ?? {});
  return data;
}
/** Ouvre/télécharge la fiche d'enregistrement INTERNE (encaissements / G50). */
export async function openFactureVentePdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/factures-vente/${id}/pdf`, undefined, mode);
}
/** Ouvre/télécharge le DOCUMENT COMMERCIAL OFFICIEL (facture / avoir avec lignes, montant en lettres). */
export async function openFactureCommercialePdf(id: string, reference?: string | null, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/factures-vente/${id}/pdf-commercial`, undefined, mode, reference ? `${reference}.pdf` : undefined);
}
